'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Activity {
  id: string;
  type: 'task' | 'medication' | 'checkin' | 'document' | 'benefit';
  title: string;
  description: string;
  timestamp: string;
  user: {
    name: string;
    avatar: string;
    alt: string;
  };
  metadata?: {
    status?: string;
    value?: string;
  };
}

interface ActivityFeedProps {
  activities: Activity[];
}

const ActivityFeed = ({ activities }: ActivityFeedProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'task':
        return 'CheckCircleIcon';
      case 'medication':
        return 'BeakerIcon';
      case 'checkin':
        return 'PhoneIcon';
      case 'document':
        return 'DocumentTextIcon';
      case 'benefit':
        return 'GiftIcon';
      default:
        return 'BellIcon';
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'task':
        return 'text-success';
      case 'medication':
        return 'text-primary';
      case 'checkin':
        return 'text-secondary';
      case 'document':
        return 'text-accent';
      case 'benefit':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  if (!isHydrated) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-start gap-4">
            <div className="w-10 h-10 bg-muted rounded-full animate-pulse"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-muted rounded animate-pulse w-3/4"></div>
              <div className="h-3 bg-muted rounded animate-pulse w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {activities.map((activity, index) => (
        <div
          key={activity.id}
          className="flex items-start gap-4 p-4 rounded-lg hover:bg-muted/50 transition-smooth"
        >
          <div className="relative flex-shrink-0">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-muted">
              <AppImage
                src={activity.user.avatar}
                alt={activity.user.alt}
                className="w-full h-full object-cover"
              />
            </div>
            <div
              className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-card border-2 border-card flex items-center justify-center ${getActivityColor(
                activity.type
              )}`}
            >
              <Icon name={getActivityIcon(activity.type) as any} size={12} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <p className="text-sm font-caption font-medium text-foreground">
                {activity.title}
              </p>
              <span className="text-xs font-caption text-muted-foreground whitespace-nowrap">
                {activity.timestamp}
              </span>
            </div>
            <p className="text-sm font-caption text-muted-foreground mb-2">
              {activity.description}
            </p>
            {activity.metadata && (
              <div className="flex items-center gap-2">
                {activity.metadata.status && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success rounded text-xs font-caption font-medium">
                    <Icon name="CheckIcon" size={12} />
                    {activity.metadata.status}
                  </span>
                )}
                {activity.metadata.value && (
                  <span className="text-xs font-caption text-muted-foreground">
                    {activity.metadata.value}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ActivityFeed;